import React from 'react';

import { defineCatalogElement } from '@archef2000/react-planner';
import * as Three from 'three';

const WIDTH = 90;
const DEPTH = 40;
const HEIGHT = 30;

const grey = new Three.MeshLambertMaterial({ color: 0xd9d7d7 });
grey.side = Three.DoubleSide;
const darkGrey = new Three.MeshLambertMaterial({ color: 0x808287 });
darkGrey.side = Three.DoubleSide;
const black = new Three.MeshLambertMaterial({ color: 0x000000 });
black.side = Three.DoubleSide;

const objectMaxLOD = makeObjectMaxLOD();
const objectMinLOD = makeObjectMinLOD();

function makeObjectMaxLOD() {
  const air_conditioner = new Three.Mesh();

  const roundedRectShape = new Three.Shape();

  const x = 0;
  const y = 0;
  const width = 0.15;
  const height = 0.6;
  const radius = 0.15;

  roundedRectShape.moveTo(x, y);
  roundedRectShape.lineTo(x + width, y);
  roundedRectShape.lineTo(x + width + radius, y + radius);
  roundedRectShape.quadraticCurveTo(
    x + width + radius,
    y + height,
    x + width / 2,
    y + height
  );
  roundedRectShape.lineTo(x + width / 2, y + height);
  roundedRectShape.lineTo(x, y + height);

  const extrudeSettings = {
    steps: 2,
    depth: 1,
    bevelEnabled: false,
    bevelThickness: 1,
    bevelSize: 1,
    bevelSegments: 1
  };

  const bodyGeometry = new Three.ExtrudeGeometry(
    roundedRectShape,
    extrudeSettings
  );
  const body = new Three.Mesh(bodyGeometry, grey);

  body.position.set(-0.11, 1.2, 0);
  body.rotation.z += Math.PI;
  air_conditioner.add(body);

  let j = 1.18;

  for (let i = -0.3; i > -0.36; i -= 0.005) {
    const gridHorizontalGeometry = new Three.BoxGeometry(0.001, 0.025, 0.705);
    const gridHorizontal = new Three.Mesh(gridHorizontalGeometry, darkGrey);
    gridHorizontal.position.set(i, j, 0.5);
    gridHorizontal.rotation.z += Math.PI / 4;
    air_conditioner.add(gridHorizontal);
    j -= 0.005;
  }

  for (let k = 0.15; k < 0.87; k += 0.05) {
    const gridVerticalGeometry = new Three.BoxGeometry(0.079, 0.025, 0.005);
    const gridVertical = new Three.Mesh(gridVerticalGeometry, darkGrey);
    gridVertical.position.set(-0.324, 1.148, k);
    gridVertical.rotation.z += Math.PI / 4;
    air_conditioner.add(gridVertical);
  }

  const roundedRectShape2 = new Three.Shape();

  const x2 = 0;
  const y2 = 0;
  const width2 = 0.2;
  const height2 = 0.4;
  const radius2 = 0.15;

  roundedRectShape2.moveTo(x2, y2);
  roundedRectShape2.lineTo(x2 + width2, y2);
  roundedRectShape2.quadraticCurveTo(
    x2 + width2 + radius2,
    y2 + height2,
    x2 + width2 / 2,
    y2 + height2
  );
  roundedRectShape2.lineTo(x2 + width2 / 2, y2 + height2);
  roundedRectShape2.quadraticCurveTo(
    x2 + width2 + radius2,
    y2 + height2 / 4,
    x2,
    y2
  );

  const extrudeSettings2 = {
    steps: 2,
    depth: 1,
    bevelEnabled: false,
    bevelThickness: 1,
    bevelSize: 1,
    bevelSegments: 1
  };

  const frontCoverGeometry = new Three.ExtrudeGeometry(
    roundedRectShape2,
    extrudeSettings2
  );
  const frontCover = new Three.Mesh(frontCoverGeometry, grey);

  frontCover.position.set(-0.2, 1.1, 0);
  frontCover.rotation.z += Math.PI;
  air_conditioner.add(frontCover);

  const roundedRectShape3 = new Three.Shape();

  const x3 = 0;
  const y3 = 0;
  const width3 = 0.1;
  const height3 = 0.1;
  const radius3 = 0.15;

  roundedRectShape3.moveTo(x3, y3);
  roundedRectShape3.quadraticCurveTo(
    x3 - width3 / 2 + radius3 / 2,
    y3 - height3,
    x3 + width3,
    y3
  );
  roundedRectShape3.lineTo(x3 + width3, y3);
  roundedRectShape3.quadraticCurveTo(
    x3 + width3 / 2 + radius3 / 2,
    y3 + 2 * height3,
    x3 + width3 / 2,
    y3 + height3
  );
  roundedRectShape3.lineTo(x3 + width3 / 2, y3 + height3);
  roundedRectShape3.quadraticCurveTo(x3 + width3 / 4, y3 + height3 / 6, x3, y3);

  const extrudeSettings3 = {
    steps: 2,
    depth: 0.1,
    bevelEnabled: false,
    bevelThickness: 1,
    bevelSize: 1,
    bevelSegments: 1
  };

  const flapSupportGeometry = new Three.ExtrudeGeometry(
    roundedRectShape3,
    extrudeSettings3
  );
  const flapLeft = new Three.Mesh(flapSupportGeometry, darkGrey);

  flapLeft.position.set(-0.27, 0.62, 0.1);
  flapLeft.rotation.y += Math.PI;
  flapLeft.rotation.z -= Math.PI / 9;
  air_conditioner.add(flapLeft);

  const flapRight = new Three.Mesh(flapSupportGeometry, darkGrey);

  flapRight.position.set(-0.27, 0.62, 1);
  flapRight.rotation.y += Math.PI;
  flapRight.rotation.z -= Math.PI / 9;
  air_conditioner.add(flapRight);

  const points2: Three.Vector2[] = [];

  points2.push(new Three.Vector2(0.5, 0));
  points2.push(new Three.Vector2(0.5, 0));
  points2.push(new Three.Vector2(0.5, 0.8));
  points2.push(new Three.Vector2(0.5, 0.8));

  const flapGeometry = new Three.LatheGeometry(
    points2,
    200,
    Math.PI / 2,
    Math.PI / 16
  );
  const flap1 = new Three.Mesh(flapGeometry, darkGrey);

  flap1.position.set(-0.4, 0.18, 0.9);
  flap1.rotation.z += Math.PI / 2;
  flap1.rotation.y += -Math.PI / 2;

  air_conditioner.add(flap1);

  const flap2 = new Three.Mesh(flapGeometry, darkGrey);

  flap2.position.set(-0.4, 0.15, 0.9);
  flap2.rotation.z += Math.PI / 2;
  flap2.rotation.y += -Math.PI / 2;

  air_conditioner.add(flap2);

  return air_conditioner;
}

function makeObjectMinLOD() {
  const air_conditioner = new Three.Mesh();

  const roundedRectShape = new Three.Shape();

  const x = 0;
  const y = 0;
  const width = 0.15;
  const height = 0.6;
  const radius = 0.15;

  roundedRectShape.moveTo(x, y);
  roundedRectShape.lineTo(x + width, y);
  roundedRectShape.lineTo(x + width + radius, y + radius);
  roundedRectShape.quadraticCurveTo(
    x + width + radius,
    y + height,
    x + width / 2,
    y + height
  );
  roundedRectShape.lineTo(x + width / 2, y + height);
  roundedRectShape.lineTo(x, y + height);

  const extrudeSettings = {
    steps: 2,
    depth: 1,
    bevelEnabled: false,
    bevelThickness: 1,
    bevelSize: 1,
    bevelSegments: 1
  };

  const bodyGeometry = new Three.ExtrudeGeometry(
    roundedRectShape,
    extrudeSettings
  );
  const body = new Three.Mesh(bodyGeometry, grey);

  body.position.set(-0.11, 1.2, 0);
  body.rotation.z += Math.PI;
  air_conditioner.add(body);

  const roundedRectShape2 = new Three.Shape();

  const x2 = 0;
  const y2 = 0;
  const width2 = 0.2;
  const height2 = 0.4;
  const radius2 = 0.15;

  roundedRectShape2.moveTo(x2, y2);
  roundedRectShape2.lineTo(x2 + width2, y2);
  roundedRectShape2.quadraticCurveTo(
    x2 + width2 + radius2,
    y2 + height2,
    x2 + width2 / 2,
    y2 + height2
  );
  roundedRectShape2.lineTo(x2 + width2 / 2, y2 + height2);
  roundedRectShape2.quadraticCurveTo(
    x2 + width2 + radius2,
    y2 + height2 / 4,
    x2,
    y2
  );

  const extrudeSettings2 = {
    steps: 2,
    depth: 1,
    bevelEnabled: false,
    bevelThickness: 1,
    bevelSize: 1,
    bevelSegments: 1
  };

  const frontCoverGeometry = new Three.ExtrudeGeometry(
    roundedRectShape2,
    extrudeSettings2
  );
  const frontCover = new Three.Mesh(frontCoverGeometry, grey);

  frontCover.position.set(-0.2, 1.1, 0);
  frontCover.rotation.z += Math.PI;
  air_conditioner.add(frontCover);

  return air_conditioner;
}

export default defineCatalogElement({
  name: 'conditioner',
  prototype: 'items',

  info: {
    tag: ['furnishings', 'metal'],
    title: 'air conditioner',
    description: 'air conditioner',
    image: require('./air_conditioner.png')
  },
  properties: {
    altitude: {
      label: 'quota',
      type: 'length-measure',
      defaultValue: {
        length: 220
      }
    }
  },

  render2D: function (element, layer, scene) {
    const angle = element.rotation + 90;

    let textRotation = 0;
    if (Math.sin((angle * Math.PI) / 180) < 0) {
      textRotation = 180;
    }

    return (
      <g transform={`translate(${-WIDTH / 2},${-DEPTH / 2})`}>
        <rect
          key="1"
          x="0"
          y="0"
          width={WIDTH}
          height={DEPTH}
          style={{
            stroke: element.selected ? '#0096fd' : '#000',
            strokeWidth: '2px',
            fill: '#84e1ce'
          }}
        />
        <text
          key="2"
          x="0"
          y="0"
          transform={`translate(${WIDTH / 2}, ${DEPTH / 2}) scale(1,-1) rotate(${textRotation})`}
          style={{ textAnchor: 'middle', fontSize: '11px' }}
        >
          {element.type}
        </text>
      </g>
    );
  },

  async render3D(element, layer, scene) {
    const newAltitude = element.properties.altitude.length;

    /*************** lod max *******************/

    const air_conditionerMaxLOD = new Three.Object3D();
    air_conditionerMaxLOD.add(objectMaxLOD.clone());

    const value = new Three.Box3().setFromObject(air_conditionerMaxLOD);

    const deltaX = Math.abs(value.max.x - value.min.x);
    const deltaY = Math.abs(value.max.y - value.min.y);
    const deltaZ = Math.abs(value.max.z - value.min.z);

    air_conditionerMaxLOD.position.x += WIDTH / 2.2;
    air_conditionerMaxLOD.position.z += DEPTH / 1.2;
    air_conditionerMaxLOD.position.y += newAltitude;
    air_conditionerMaxLOD.rotation.y += -Math.PI / 2;
    air_conditionerMaxLOD.scale.set(
      WIDTH / deltaZ,
      HEIGHT / deltaY,
      DEPTH / deltaX / 1.4
    );

    /*************** lod min *******************/

    const air_conditionerMinLOD = new Three.Object3D();
    air_conditionerMinLOD.add(objectMinLOD.clone());
    air_conditionerMinLOD.position.x += WIDTH / 2.2;
    air_conditionerMinLOD.position.z += DEPTH / 1.2;
    air_conditionerMinLOD.position.y += newAltitude;
    air_conditionerMinLOD.rotation.y += -Math.PI / 2;
    air_conditionerMinLOD.scale.set(
      WIDTH / deltaZ,
      HEIGHT / deltaY,
      DEPTH / deltaX / 1.4
    );

    /**** all level of detail ***/

    const lod = new Three.LOD();

    lod.addLevel(air_conditionerMaxLOD, 200);
    lod.addLevel(air_conditionerMinLOD, 900);
    lod.updateMatrix();
    lod.matrixAutoUpdate = false;

    if (element.selected) {
      const bbox = new Three.BoxHelper(lod, 0x99c3fb);
      bbox.material.linewidth = 5;
      bbox.renderOrder = 1000;
      bbox.material.depthTest = false;
      lod.add(bbox);
    }
    return lod;
  }
});
